import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BasicDatePicker from '@mui/lab/DatePicker';
import BasicDateTimePicker from '@mui/lab/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DATE_FORMAT } from '@cyberint/common-utils';
import { PopperPlacements, INPUT_PROPS } from './consts';
import * as S from './styled';

const POPPER_SX = {
  sx: {
    '& .MuiPaper-root': {
      backgroundColor: 'var(--calendar-popup-bg, #fff)',
      color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))'
    },
    '& .MuiPickersDay-root, & .MuiDateRangePickerDay-day': { color: 'var(--calendar-popup-color, inherit)' },
    // MUI paints idle days with theme.palette.background.paper (white); the popup surface shows through instead
    '& .MuiPickersDay-root:not(.Mui-selected)': { backgroundColor: 'transparent' },
    '& .MuiPickersDay-root:not(.Mui-selected):hover': {
      backgroundColor: 'var(--calendar-popup-hover-bg, rgba(0, 0, 0, 0.04))'
    },
    '& .MuiIconButton-root': { color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.54))' },
    // clock numbers are an unclassed span (PrivateClockNumber) — only role/aria identify them
    '& [role="option"]:not([aria-selected="true"]):not([aria-disabled="true"])': {
      color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))'
    },
    '& .MuiDivider-root': { borderColor: 'var(--calendar-popup-divider-color, rgba(0, 0, 0, 0.12))' },
    '& .MuiPickersDay-today': { borderColor: 'var(--calendar-popup-color, currentColor)' },
    '& .MuiTab-root:not(.Mui-selected)': { color: 'var(--calendar-popup-color, rgba(0, 0, 0, 0.87))' },
    '& .MuiPickersCalendarHeader-label': {
      color: 'var(--calendar-popup-color, inherit)'
    },
    '& .MuiTypography-caption[aria-hidden="true"]': { color: 'var(--calendar-popup-muted-color, rgba(0, 0, 0, 0.6))' },
    '& .MuiDateRangePickerViewDesktop-rangeCalendarContainer': {
      borderColor: 'var(--calendar-popup-divider-color, rgba(0, 0, 0, 0.12))'
    },
    '& .MuiDateRangePickerDay-day.Mui-selected, & .MuiPickersDay-root.Mui-selected': {
      color: 'var(--calendar-day-color-selected, #fff)'
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: 'var(--calendar-day-bg-selected)'
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: 'var(--calendar-day-bg-selected-hover)'
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
      backgroundColor: 'var(--calendar-day-bg-selected)'
    }
  }
};

const DatePicker = ({
  onChange,
  onBlur,
  onFocus,
  onClose,
  isOpen = false,
  error,
  value,
  clearable,
  withTime = false,
  dateFormat,
  placement = PopperPlacements.AUTO,
  height,
  width,
  inputProps,
  disabled,
  loading,
  minDate,
  maxDate
}) => {
  const [isOpenInternal, setIsOpen] = useState(isOpen);
  const Picker = withTime ? BasicDateTimePicker : BasicDatePicker;
  const inputFormat = dateFormat || (withTime ? DATE_FORMAT.MMM_DD_YYYY_HH_MM_FORMAT : DATE_FORMAT.MMM_DD_YYYY_FORMAT);

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  const onCloseHandler = useCallback(
    e => {
      setIsOpen(false);
      onClose?.(e);
    },
    [onClose]
  );

  const computedValue = useMemo(() => (typeof value === 'string' ? new Date(value) : value), [value]);
  const popperProps = useMemo(() => ({ placement, ...POPPER_SX }), [placement]);
  const memoInputProps = useMemo(() => ({ ...INPUT_PROPS, ...inputProps }), [inputProps]);
  const onChangeHandler = useCallback(
    (v, strValue) => onChange(typeof v?.valueOf === 'function' ? v.valueOf() : v, strValue),
    [onChange]
  );

  const inputRenderer = useCallback(
    props => (
      <S.TextFieldWrapper
        className="datePickerInput"
        {...props}
        size="small"
        onBlur={onBlur}
        onFocus={onFocus}
        error={error}
        $inputWidth={width}
        InputProps={memoInputProps}
        onClick={() => setIsOpen(prev => !prev)}
      />
    ),
    [width, memoInputProps, onBlur, onFocus, error]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <S.DatePickerWrapper $width={width} $height={height}>
        <Picker
          clearable={clearable}
          disabled={disabled}
          error={error}
          value={computedValue}
          onChange={onChangeHandler}
          PopperProps={popperProps}
          disableMaskedInput
          inputFormat={inputFormat}
          renderInput={inputRenderer}
          onClose={onCloseHandler}
          open={isOpenInternal}
          minDate={minDate}
          maxDate={maxDate}
        />
        {loading && <S.LoadingIcon size={16} color="var(--button-loading-icon-color)" rotate />}
      </S.DatePickerWrapper>
    </LocalizationProvider>
  );
};

DatePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  isOpen: PropTypes.bool,
  error: PropTypes.bool,
  clearable: PropTypes.bool,
  withTime: PropTypes.bool,
  inputProps: PropTypes.shape({}),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  dateFormat: PropTypes.string,
  placement: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  width: PropTypes.string,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number])
};

export default DatePicker;
